/* eslint-disable jsx-a11y/label-has-associated-control */
// this is taken from plugin: https://github.com/mmw/react-password-strength#readme
/* ignore jslint start */

import React, { useState, useEffect, useRef } from 'react'

import {
  classnames,
  Icon,
  VisibilityOn,
  VisibilityOff,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func,
  bool,
  string,
  number,
  array,
  object,
} from 'prop-types'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

let zxcvbn

const isTooShort = (password, minLength) => password.length < minLength
const DEFAULT_SCORE_WORDS = ['weak', 'okay', 'good', 'strong']

function ReactPasswordStrength({
  changeCallback = null,
  minScore = 2,
  userInputs = EMPTY_ARRAY,
  minLength = 5,
  scoreWords = DEFAULT_SCORE_WORDS,
  inputProps = EMPTY_OBJECT,
  className = '',
  inputClassName = '',
  tooShortWord = 'too short',
  helpText = '',
  hasError = false,
  showToggleVisibility = false,
}) {
  const reactPasswordStrengthInput = useRef(null)
  const [currentState, setState] = useState({
    score: 0,
    isValid: false,
    password: '',
    isLoaded: false,
  })
  const [showPassword, setShowPassword] = useState(false)

  function toggleShowPassword() {
    setShowPassword(!showPassword)
  }

  useEffect(() => {
    import('zxcvbn').then(module => {
      zxcvbn = module.default
      setState((prevState) => ({ ...prevState, isLoaded: true }))
    })
  }, [])

  function handleChange() {
    const password = reactPasswordStrengthInput.current.value

    let score = 0
    let result = null

    // always sets a zero score when min length requirement is not met
    // avoids unnecessary zxcvbn computations (CPU intensive)
    if (isTooShort(password, minLength) === false) {
      result = zxcvbn(password, userInputs)
      score = result.score
    }

    const newState = {
      ...currentState,
      isValid: score >= minScore,
      password,
      score,
    }

    setState(newState)

    if (changeCallback !== null) {
      changeCallback(newState, result)
    }
  }

  const wrapperClasses = [
    'ReactPasswordStrength',
    'new-password-strength',
    className || '',
    currentState.password.length > 0 ? `is-strength-${currentState.score}` : '',
    showToggleVisibility ? 'has-toggle' : '',
  ]

  const strengthDesc = (
    isTooShort(currentState.password, minLength)
      ? tooShortWord
      : scoreWords[currentState.score]
  )

  const footerClass = hasError ? 'cgt-legacy-color-utility-danger-100' : 'cgt-legacy-text--disabled'

  return (
    <div className="auth-tab__field cgt-form-element-wrapper cgt-form-element-wrapper--medium">
      <div className="cgt-form-element-header">
        <label
          htmlFor={inputProps.id}
          className="cgt-form-element-header__title cgt-legacy-text-caption"
        >
          Password <b>*</b>
        </label>
      </div>
      <div className={wrapperClasses.join(' ')}>
        <input
          type={showPassword ? 'text' : 'password'}
          {...inputProps}
          disabled={!currentState.isLoaded}
          className={classnames(
            'ReactPasswordStrength-input cgt-password-strength__input new-password-input',
            currentState.isValid === true && 'is-password-valid',
            currentState.password.length > 0 && !showPassword && 'is-password-invalid',
            inputClassName
          )}
          onChange={handleChange}
          onBlur={handleChange}
          ref={reactPasswordStrengthInput}
        />
        <div className="ReactPasswordStrength-strength-bar new-password-bar" />
        <span className="ReactPasswordStrength-strength-desc new-password-state">{strengthDesc}</span>
        {showToggleVisibility && (
          <Icon
            svgComponent={showPassword ? VisibilityOff : VisibilityOn}
            color="dark"
            onClick={toggleShowPassword}
            className="new-password-toggle"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          />
        )}
      </div>
      <div className={`cgt-form-element-footer cgt-legacy-text-caption ${footerClass}`}>
        {helpText}
      </div>
    </div>
  )
}

ReactPasswordStrength.propTypes = {
  changeCallback: func,
  className: string,
  inputClassName: string,
  defaultValue: string,
  inputProps: object,
  minLength: number,
  minScore: number,
  scoreWords: array,
  tooShortWord: string,
  userInputs: array,
  helpText: string,
  hasError: bool,
  showToggleVisibility: bool,
}

export default ReactPasswordStrength
